import { useState } from 'react';
import type { UserGroup } from '@ehr/shared';
import { api } from '../lib/api.js';
import { askConfirm } from '../lib/dialogs.js';
import { Shell, msg, newId, uname, type TeamUser } from './usersShared.js';
import './usersModal.css';

/**
 * User Groups (EHR_08-14-V38 renderGroupsManager, ehr#60): groups on the left, members on
 * the right. Used later by Distribution, Redistribution and internal messaging; today the
 * groups show as chips on each row of Users & Profile. Saves with Save.
 */
const CHECK = (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

export function UserGroupsWindow({
  groups: initial,
  users,
  roleLabel,
  onBack,
  onSaved,
}: {
  groups: UserGroup[];
  users: TeamUser[];
  roleLabel: (u: TeamUser) => string;
  onBack: () => void;
  onSaved: () => void;
}) {
  const [groups, setGroups] = useState<UserGroup[]>(initial);
  const [sel, setSel] = useState<string | null>(initial[0]?.id ?? null);
  const [q, setQ] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const dirty = JSON.stringify(groups) !== JSON.stringify(initial);
  const g = groups.find((x) => x.id === sel) ?? null;
  const setG = (patch: Partial<UserGroup>) =>
    setGroups(groups.map((x) => (x.id === sel ? { ...x, ...patch } : x)));

  const add = () => {
    let name = 'New group';
    for (let i = 2; groups.some((x) => x.name.toLowerCase() === name.toLowerCase()); i++)
      name = `New group ${i}`;
    const id = newId('ugr');
    setGroups([...groups, { id, name, memberIds: [] }]);
    setSel(id);
    setQ('');
  };
  const remove = async () => {
    if (!g) return;
    if (
      !(await askConfirm(
        `Delete the group “${g.name}”? Members keep their other groups; only this group is removed.`,
      ))
    )
      return;
    const rest = groups.filter((x) => x.id !== g.id);
    setGroups(rest);
    setSel(rest[0]?.id ?? null);
  };
  const toggle = (uid: string) => {
    if (!g) return;
    setG({
      memberIds: g.memberIds.includes(uid)
        ? g.memberIds.filter((m) => m !== uid)
        : [...g.memberIds, uid],
    });
  };
  const save = () => {
    const names = groups.map((x) => x.name.trim().toLowerCase());
    if (names.some((n) => !n)) return setErr('Every group needs a name.');
    if (new Set(names).size !== names.length) return setErr('Two groups have the same name.');
    setSaving(true);
    setErr(null);
    api
      .put('/settings/team/groups', {
        groups: groups.map((x) => ({ ...x, name: x.name.trim() })),
      })
      .then(() => {
        onSaved();
        onBack();
      })
      .catch((e: unknown) => setErr(msg(e)))
      .finally(() => setSaving(false));
  };

  const ql = q.trim().toLowerCase();
  const shown = users.filter(
    (u) => !ql || [uname(u), roleLabel(u), u.title ?? ''].some((s) => s.toLowerCase().includes(ql)),
  );

  return (
    <Shell
      title="Groups"
      sub="Create groups and add users — used by Distribution, Redistribution, and the Internal Messaging module."
      onClose={onBack}
      foot={
        <>
          <button className="btn" onClick={onBack}>
            {dirty ? 'Cancel' : 'Close'}
          </button>
          <button className="btn primary" disabled={!dirty || saving} onClick={save}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {err && <div className="config-err">{err}</div>}
      <div className="gm-wrap">
        <div className="gm-left">
          <button className="btn primary gm-new" onClick={add}>
            + New group
          </button>
          {groups.length === 0 ? (
            <div className="gm-empty">No groups yet — create one above.</div>
          ) : (
            groups.map((x) => (
              <button
                key={x.id}
                className={`gm-grp${x.id === sel ? ' active' : ''}`}
                onClick={() => {
                  setSel(x.id);
                  setQ('');
                }}
              >
                <span className="gm-grp-name">{x.name || '(unnamed)'}</span>
                <span className="gm-grp-count">{x.memberIds.length}</span>
              </button>
            ))
          )}
        </div>
        <div className="gm-right">
          {!g ? (
            <div className="gm-empty gm-empty-lg">
              Create or pick a group on the left to add members.
            </div>
          ) : (
            <>
              <div className="gm-right-head">
                <input
                  className="gm-name-input"
                  value={g.name}
                  maxLength={60}
                  aria-label="Group name"
                  onChange={(e) => setG({ name: e.target.value })}
                />
                <span className="gm-members">
                  {g.memberIds.length} member{g.memberIds.length === 1 ? '' : 's'}
                </span>
                <button className="btn gm-del" onClick={remove}>
                  Delete
                </button>
              </div>
              <input
                className="gm-search"
                placeholder="Search users by name, role, or title…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
              <div className="gm-users">
                {shown.length === 0 ? (
                  <div className="gm-empty">No users match “{q}”.</div>
                ) : (
                  shown.map((u) => {
                    const inG = g.memberIds.includes(u.id);
                    return (
                      <button
                        key={u.id}
                        className={`gm-user${inG ? ' in' : ''}`}
                        aria-pressed={inG}
                        onClick={() => toggle(u.id)}
                      >
                        <span className="gm-user-check">{inG ? CHECK : null}</span>
                        <span className="gm-user-main">
                          <span className="gm-user-name">{uname(u)}</span>
                          <span className="gm-user-meta">
                            {[roleLabel(u), u.title].filter(Boolean).join(' · ') || '—'}
                          </span>
                        </span>
                      </button>
                    );
                  })
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </Shell>
  );
}
